import type { ReactNode } from 'react';
import { Section } from '@/components/layout';
import { Reveal } from '@/components/motion';
import type { TourEvent } from '@/features/events';
import {
  IconArcosOndas,
  IconCorazonRafaga,
  IconCorazonRafagaAlt,
  IconFlorDeLaVida,
} from '../icons/BogotaIcons';
import styles from './BenefitsRowBogota.module.css';

/* =============================================================================
 * S2 · BANDA DE BENEFICIOS  (mockup y 638–790 · 41,5–51,4 % del alto)
 * -----------------------------------------------------------------------------
 * 4 columnas separadas por filetes dorados verticales que NO llegan a los bordes
 * de la banda (≈60 % de la altura, centrados) — medido sobre el PNG.
 *
 * El TEXTO sale del dato tipado (`event.benefits`), no de esta página.
 * Los ICONOS son míos (SVG dibujado): el `icon` del dato es genérico del
 * substrato (`heart`, `mountain`, `seed`, `star`) y el mockup de Bogotá pide
 * cuatro piezas específicas — corazón con ráfaga, arcos sobre montaña, flor de
 * la vida y corazón con ráfaga larga.
 *
 * ASIMETRÍA DELIBERADA, calcada del mockup: en las celdas 1 y 4 la segunda línea
 * va en CAJA BAJA; en las celdas 2 y 3 va en VERSALES. No se homogeneiza.
 * ========================================================================== */

const ICONS: readonly ReactNode[] = [
  <IconCorazonRafaga key="1" />,
  <IconArcosOndas key="2" />,
  <IconFlorDeLaVida key="3" />,
  <IconCorazonRafagaAlt key="4" />,
];

/** Celdas cuya segunda línea va en versales (2 y 3 del mockup). */
const UPPERCASE_DESCRIPTION = new Set(['bo-alineacion', 'bo-linaje']);

export function BenefitsRowBogota({ event }: { event: TourEvent }) {
  return (
    <Section background="surface" spacing="tight" width="default" className={styles.section}>
      <ul className={styles.grid}>
        {event.benefits.map((benefit, index) => (
          <Reveal key={benefit.id} as="li" delay={index * 0.07} className={styles.cell}>
            <span className={styles.icon} aria-hidden="true">
              {ICONS[index]}
            </span>
            <p className={styles.title}>{benefit.title}</p>
            {benefit.description ? (
              <p
                className={
                  UPPERCASE_DESCRIPTION.has(benefit.id) ? styles.descriptionCaps : styles.description
                }
              >
                {benefit.description}
              </p>
            ) : null}
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}
